import React, { useEffect } from 'react'
import { useLocation } from 'react-router-dom';
import { Container, Table } from 'react-bootstrap';
import styled from "styled-components";

const WtmContainer = styled(Container)`
  [id] {
    scroll-margin-top: 180px;
  }
  h2, h3 {
    margin-top: 35px !important;
  }
  h3 {
    color: #1178ba;    
  }
  hr {
    margin: 35px 0 !important;
  }
`

const WhyThisMatters = () => {
  const location = useLocation()

  useEffect(() => {
    if (!location.hash) return;
    const el = document.getElementById(location.hash.slice(1));
    if (el) el.scrollIntoView();
  }, [location.hash]) 

  return (
    <WtmContainer>
      <h2 id="name-the-face">Name The Face</h2>
      <h3>How it Works</h3>
      <p>Name the Face presents a set of face-name pairs to study, then asks the participant to match them from memory. As levels increase, the number of pairs in each set increases, and names become phonetically similar.  This challenges the brain’s ability to encode, hold, and retrieve face-name associations.</p>
      <h3>Why This is Important: Making Functional Connections</h3>
      <ul>
        <li>Remembering the names of new neighbors, caregivers, or medical staff</li>
        <li>Recognizing familiar faces in community or social settings</li>
        <li>Learning and recalling names of new acquaintances or friends</li>
        <li>Managing social obligations that depend on name-face recall (family events, support groups)</li>
        <li>Navigating environments where recognizing personnel matters</li>
        <li>Reducing reliance on others for social identification cues</li>
      </ul>
      <h3>Cognitive Domains Targeted</h3>
      <Table striped responsive>
        <tbody>
          <tr>
            <td>
              <strong>PRIMARY</strong><br />
              <strong>Associative memory</strong><br />
              Forming and retrieving links between two unrelated items — a face and a name
            </td>
            <td>
              <strong>PRIMARY</strong><br />
              <strong>Working memory</strong><br />
              Holding face-name pairs in mind during the study phase while preparing to act on them
            </td>
          </tr>
          <tr>
            <td>
              <strong>SECONDARY</strong><br />
              <strong>Visual recognition</strong><br />
              Discriminating between faces, especially same-gender faces at Advanced and Expert levels
            </td>
            <td>
              <strong>SECONDARY</strong><br />
              <strong>Interference management</strong><br />
              Suppressing similar-sounding name competitors to retrieve the correct response (Expert)
            </td>
          </tr>
          <tr>
            <td>
              <strong>SECONDARY</strong><br />
              <strong>Attention and encoding strategy</strong>
              Directing focus during study to form durable memory traces
            </td>
            <td>
              <strong>SECONDARY</strong><br />
              <strong>Processing speed</strong><br />
              Efficiency of encoding and retrieval across progressively larger sets
            </td>
          </tr>
        </tbody>
      </Table>
      <h3>The Neuroscience</h3>
      <p>Face-name binding is one of the most demanding memory tasks the human brain performs. Faces are processed in the fusiform face area (FFA) of the temporal lobe, while names — as arbitrary verbal labels — are handled by left-lateralized language networks. Linking these two distinct representations requires the hippocampus to form an associative memory trace that bridges the visual and verbal systems. The prefrontal cortex maintains that trace in working memory during the study phase and monitors retrieval accuracy during matching. At higher levels, where names begin with the same letter or sound alike, the anterior cingulate cortex becomes increasingly engaged as the brain manages interference — suppressing competing responses to select the correct name.</p>
      <hr />
      <h2 id="memory-match">Memory Match</h2>
      <h3>How it Works</h3>
      <p>Memory Match presents an array of cards that are all face-down at the start of the activity.  Each card flip is a blind choice that reveals an image and the participant is challenged to remember what was seen and where, before the card returns to its “face-down” position. Over successive turns, participants build a mental map of the grid, holding accumulating location-image pairs in memory while simultaneously trying to find matches. As the levels increase, the number of cards increases, along with image complexity (from clear, distinct images to highly similar ones that require precise visual discrimination).</p>
      <h3>Why This is Important: Making Functional Connections</h3>
      <ul>
        <li>Learning the layout of a new environment — a new home, senior living facility, or unfamiliar store (grocery store)</li>
        <li>Locating items whose positions change (medications moved, keys in an unfamiliar spot)</li>
        <li>Navigating digital interfaces and menus encountered for the first time</li>
        <li>Keeping track of which tasks have been completed in a multi-step sequence</li>
        <li>Following multi-step visual procedures — assembly, cooking, or medication preparation — from memory</li>
        <li>Remembering what was said or shown earlier in a conversation without written notes</li>
      </ul>
      <h3>Cognitive Domains Targeted</h3>
      <Table striped responsive>
        <tbody>
          <tr>
            <td>
              <strong>PRIMARY</strong><br />
              <strong>Visuospatial working memory</strong><br />
              Building and updating a mental map of card locations with no starting information, turn by turn
            </td>
            <td>
              <strong>PRIMARY</strong><br />
              <strong>Visual episodic memory</strong><br />
              Forming and retrieving image-location pairings from specific prior moments, under ongoing time pressure
            </td>
          </tr>
          <tr>
            <td>
              <strong>PRIMARY</strong><br />
              <strong>Sustained and selective attention</strong><br />
              Rapid, focused attention to image identity and grid location on every card flip throughout the task
            </td>
            <td>
              <strong>SECONDARY</strong><br />
              <strong>Visual discrimination</strong><br />
              Distinguishing between similar images at higher difficulty levels
            </td>
          </tr>
          <tr>
            <td>
              <strong>SECONDARY</strong><br />
              <strong>Strategic planning</strong><br />
              Developing systematic search strategies to build the memory map more efficiently
            </td>
            <td>
              <strong>SECONDARY</strong><br />
              <strong>Inhibitory control</strong><br />
              Suppressing the impulse to guess randomly and instead using stored location knowledge to guide selections
            </td>
          </tr>
        </tbody>
      </Table>
      <h3>The Neuroscience</h3>
      <p>Because all cards begin face-down, there is no encoding phase before play — the brain must build its spatial memory map concurrently with retrieval attempts, making this a particularly demanding exercise. Each card flip engages the dorsal attention network (intraparietal sulcus and frontal eye fields) to rapidly bind a visual image to a grid location. The hippocampus and parahippocampal cortex must immediately consolidate that binding into an episodic trace before the card flips back. The prefrontal cortex then maintains an active working memory register of all known locations while directing strategic search.</p>
      <p>Critically, encoding and retrieval are not sequential here — they overlap on every turn. When a second card is flipped and matches something seen earlier, the brain must simultaneously retrieve the prior location, suppress the impulse to guess randomly, and update the spatial map with new information. This concurrent load on the hippocampal-prefrontal circuit closely mirrors the associative memory demands implicated in early cognitive change. At higher difficulty levels, the ventral visual stream must resolve fine-grained image differences, and the anterior cingulate cortex manages rising interference between similar-looking cards.</p>
      <hr />
      <h2 id="flip-the-image">Flip the Image</h2>
      <h3>How it Works</h3>
      <p>When you flip an image in your mind without touching it, you are using one of the brain’s most powerful spatial skills — the ability to mentally transform what you see. This is the same skill used when reading a map, figuring out which way a piece fits in a puzzle, or understanding a diagram in an instruction manual.</p>
      <p>Flip the Image presents a target image in the top row and three choices in a row beneath it. The participant must mentally transform the target — flipping it vertically (upside-down) or horizontally (left-right) at the lower levels.  As the exercise progresses, the image complexity increases from simple geometric outlines to complex compositions.</p>
      <h3>Why This is Important: Making Functional Connections </h3>
      <ul>
        <li>Reversing a vehicle or navigating using a backup camera, which displays a mirror image of the actual environment</li>
        <li>Reading floor plans, sewing patterns, or technical diagrams where spatial orientation must be mentally adjusted</li>
        <li>Reading maps and orienting to a new environment when the map is rotated relative to your direction of travel</li>
        <li>Following assembly or furniture instructions that require mentally rotating parts to understand how they fit</li>
        <li>Interpreting reflected or reversed images — mirrors, reversing cameras, medical imaging displays</li>
        <li>Spatial reasoning in home management — arranging furniture, packing, organizing storage</li>
        <li>Recognizing whether a sign or label is correctly oriented or has been placed upside-down or reversed</li>
        <li>Navigating a building or facility by mentally relating a floor plan to the physical environment</li>
      </ul>
      <h3>Cognitive Domains Targeted</h3>
      <Table striped responsive>
        <tbody>
          <tr>
            <td>
              <strong>PRIMARY</strong><br />
              <strong>Visuospatial reasoning</strong><br />
              Mentally transforming an image along a specified axis — the defining cognitive operation of the task
            </td>
            <td>
              <strong>PRIMARY</strong><br />
              <strong>Mental rotation and reflection</strong><br />
              Applying a directed spatial transformation (vertical or horizontal flip) to an internal visual representation
            </td>
          </tr>
          <tr>
            <td>
              <strong>SECONDARY</strong><br />
              <strong>Working memory</strong><br />
              Holding the target image stable in the mind’s eye while the transformation is computed and applied
            </td>
            <td>
              <strong>SECONDARY</strong><br />
              <strong>Inhibitory control</strong><br />
              Evaluating and rejecting plausible foils; suppressing competing responses to isolate the correct choice
            </td>
          </tr>
          <tr>
            <td>
              <strong>SECONDARY</strong><br />
              <strong>Cognitive flexibility</strong><br />
              Shifting between vertical and horizontal transformation rules across level pairs
            </td>
            <td>
              <strong>SECONDARY</strong><br />
              <strong>Visual discrimination</strong><br />
              Fine-grained perceptual comparison of internally patterned images at higher levels
            </td>
          </tr>
        </tbody>
      </Table>
      <h3>The Neuroscience</h3>
      <p>Flip the Image is a mental transformation task, and its core cognitive operation — mentally rotating or reflecting an object along a spatial axis — is one of the most studied paradigms in cognitive neuroscience. The right parietal cortex, particularly the superior parietal lobule (SPL) and intraparietal sulcus (IPS), is centrally involved in applying spatial transformations to visual representations held in the mind’s eye. Age-related decline in visuospatial processing is closely linked to reduced parietal activation and gray matter integrity in these regions.</p>
      <p>The prefrontal cortex contributes working memory resources to hold the target image stable while the transformation is applied. When three foils must be evaluated, the left inferior frontal gyrus and anterior cingulate support inhibitory control. Importantly, the shift from vertical to horizontal flipping between level pairs is not merely a difficulty increment — it requires the participant to apply a qualitatively different spatial operation, taxing cognitive flexibility as well as transformation skill.</p>
      <hr />
      <h2 id="number-symbol">Number Symbol</h2>
      <h3>How it Works</h3>
      <p>This exercise asks you to memorize a set of number-symbol pairs and then recall the pairings. The number of pairings grows with each level, systematically expanding the memory load. The mental effort of holding the pairs in your head and retrieving them is exactly what strengthens the underlying memory system. By challenging the user to remember the pairings and not to rely on the answer key, it forces the participant to internalize the pairings and retrieve them from memory. </p>
      <h3>Why This is Important: Making Functional Connections</h3>
      <ul>
        <li>Learning and recalling new PINs, passwords, or access codes without writing them down</li>
        <li>Remembering which button or control corresponds to which function on a new device or appliance</li>
        <li>Associating names with room numbers, medication times with specific pills, or schedules with days</li>
        <li>Learning a new transit system — associating line colors, numbers, or symbols with destinations</li>
        <li>Following multi-step medical regimens that pair specific medications with times or conditions</li>
        <li>Adapting quickly to new systems where arbitrary codes and labels must be learned</li>
      </ul>
      <h3>Cognitive Domains Targeted</h3>
      <Table striped responsive>
        <tbody>
          <tr>
            <td>
              <strong>PRIMARY</strong><br />
              <strong>Working memory</strong><br />
              Holding and actively retrieving multiple arbitrary number-symbol pairings simultaneously, without external reference
            </td>
            <td>
              <strong>PRIMARY</strong><br />
              <strong>Associative learning</strong><br />
              Forming and consolidating arbitrary bindings between unrelated items — numbers and symbols with no pre-existing semantic link
            </td>
          </tr>
          <tr>
            <td>
              <strong>SECONDARY</strong><br />
              <strong>Sustained attention</strong><br />
              Maintaining focus and retrieval accuracy across the full sequence without performance degradation
            </td>
            <td>
              <strong>SECONDARY</strong><br />
              <strong>Inhibitory control</strong><br />
              Suppressing interference from visually similar symbols and previously retrieved pairings as the sequence progresses
            </td>
          </tr>
          <tr>
            <td>
              <strong>SECONDARY</strong><br />
              <strong>Procedural learning</strong><br />
              With repeated practice, pairings become increasingly automatic — a neuroplasticity response reflecting consolidation from effortful to fluent retrieval
            </td>
            <td>
              <strong>SECONDARY</strong><br />
              <strong>Cognitive flexibility</strong><br />
              Shifting attention across the key and the response bank as needed during the exercise
            </td>
          </tr>
        </tbody>
      </Table>
      <h3>The Neuroscience</h3>
      <p>The Digit Symbol Coding paradigm — on which this exercise is modeled — is among the most sensitive neuropsychological measures of working memory and associative learning, showing reliable change across the cognitive lifespan. Its sensitivity derives from the fact that it loads simultaneously on multiple neural systems. The prefrontal cortex maintains the paired-associate set in the phonological and visual working memory buffers while directing sequential retrieval. The hippocampus contributes to the rapid formation of arbitrary associative bindings — number-to-symbol pairings have no pre-existing semantic relationship, making them dependent on the same hippocampal binding mechanism recruited in associative memory tasks.</p>
      <p>As the number of pairings increases across levels, the demands on the central executive — the attentional control component of working memory — escalate, because more items must be held simultaneously and interference between similar-looking symbols must be managed. With repeated practice, there is evidence of procedural learning: the pairings begin to feel more automatic, reflecting a shift from effortful hippocampal retrieval toward more automatized pattern recognition — a neuroplasticity response that is precisely the therapeutic target of the exercise.</p>
      <hr />
      <h2 id="color-illusion">Color Illusion</h2>
      <h3>How it Works</h3>
      <p>Color Illusion presents cards containing color words, colored shapes, and colored backgrounds — multiple visual stimuli that actively compete with each other and with the correct response. The participant must read the target, then say the answer aloud. The defining challenge at every level is interference: the brain’s automatic reading and color-naming systems generate competing responses that must be actively suppressed in favor of the target dimension. Because there is no on-screen feedback, the participant must also self-monitor accuracy — adding a metacognitive layer to an already demanding executive task. Simply put, the brain is required to filter out unnecessary information while attending to necessary information. See the table below, to learn more about how the levels progress in difficulty.</p>
      <h3>Why This is Important: Making Functional Connections </h3>
      <ul>
        <li>Following a specific instruction while ignoring irrelevant competing information in the environment</li>
        <li>Staying on task during conversation when background noise or activity is distracting</li>
        <li>Reading a medication label accurately when multiple pieces of information compete for attention</li>
        <li>Driving or navigating — attending to relevant signs and signals while filtering visual distractors</li>
        <li>Managing competing demands simultaneously, such as listening to directions while writing them down</li>
        <li>Catching and correcting one’s own errors in daily tasks without relying on others for feedback</li>
      </ul>
      <h3>Cognitive Domains Targeted</h3>
      <Table striped responsive>
        <tbody>
          <tr>
            <td>
              <strong>PRIMARY</strong><br />
              <strong>Inhibitory control</strong><br />
              Actively suppressing automatic competing responses to select the target dimension — the core Stroop demand
            </td>
            <td>
              <strong>PRIMARY</strong><br />
              <strong>Selective attention</strong><br />
              Directing attention to one specific visual dimension while filtering all competing dimensions on the same card
            </td>
          </tr>
          <tr>
            <td>
              <strong>PRIMARY</strong><br />
              <strong>Cognitive flexibility and set shifting</strong><br />
              Applying different response rules across levels; at higher levels, shifting between multiple response targets within a single card
            </td>
            <td>
              <strong>SECONDARY</strong><br />
              <strong>Working memory</strong><br />
              Holding the multi-part task rule in mind across a full row of cards, especially at Advanced and Expert levels
            </td>
          </tr>
          <tr>
            <td>
              <strong>SECONDARY</strong><br />
              <strong>Metacognitive self-monitoring</strong><br />
              Evaluating one’s own spoken responses for accuracy without external feedback; detecting and correcting errors in real time
            </td>
            <td>
              <strong>SECONDARY</strong><br />
              <strong>Processing speed</strong><br />
              Efficiency of interference resolution across cards; Stroop performance is sensitive to the speed of prefrontal inhibitory control
            </td>
          </tr>
        </tbody>
      </Table>
      <h3>The Neuroscience</h3>
      <p>The Stroop effect — the finding that naming the ink color of a color word is slower and more error-prone than reading the word itself — is among the most robust phenomena in cognitive psychology, first described in 1935 and replicated thousands of times since. Its neural basis is well established: the left dorsolateral prefrontal cortex (dlPFC) and anterior cingulate cortex (ACC) must actively override the brain’s highly automatized word-reading system to select the correct, less-automatic response. This inhibitory control operation is effortful and sensitive to cognitive change — Stroop interference increases measurably as executive function is affected.</p>
      <p>Color Illusion extends the classical paradigm in clinically meaningful ways. At Intermediate level, the addition of a conflicting shape color requires the prefrontal cortex to manage simultaneous suppression of two independent distractor streams. At Advanced level, the task rule requires three sequential responses per card while holding a multi-part response template in working memory, recruiting the phonological loop and central executive simultaneously. At Expert level, four competing color dimensions are present, and the response rule shifts qualitatively, adding a cognitive flexibility demand mediated by the frontoparietal network. Across all levels, the self-monitoring requirement — with no external feedback — engages the medial prefrontal cortex and ACC in ongoing error detection and response evaluation.</p>
      <hr />
      <h2 id="roman-numerals">Roman Numerals</h2>
      <h3>How it Works</h3>
      <p>Roman numerals are a system with rules — and learning to work within those rules is what makes this exercise effective. To complete each task, participants are asked to apply logic, complete mental math, and perform conversions from numbers to Roman Numerals and Roman Numerals to numbers. Items are presented in randomized sets of six, with answers revealed only at the end of each set, requiring participants to commit to responses without immediate feedback. As with other exercises, Roman Numerals increases in complexity with each level. A reference key and full rule set are always accessible, but minimizing their use is what makes this exercise more demanding. </p>
      <h3>Why This is Important: Making Functional Connections</h3>
      <ul>
        <li>Mental arithmetic — calculating totals, making change, or estimating costs without a calculator</li>
        <li>Reading Roman Numerals encountered in daily life: clock faces, chapter headings, building cornerstones, film credits</li>
        <li>Learning and applying rule-based systems — tax tables, medication dosing schedules, coding conventions</li>
        <li>Following multi-step instructions that require holding intermediate results in mind</li>
        <li>Managing finances — mental budgeting, tracking expenditures, calculating balances across categories</li>
        <li>Problem-solving under uncertainty — committing to a decision without immediate confirmation, then self-checking</li>
      </ul>
      <h3>Cognitive Domains Targeted</h3>
      <Table striped responsive>
        <tbody>
          <tr>
            <td>
              <strong>PRIMARY</strong><br />
              <strong>Rule-based reasoning</strong><br />
              Applying a positional symbolic rule system — additive and subtractive — to parse and generate expressions
            </td>
            <td>
              <strong>PRIMARY</strong><br />
              <strong>Working memory</strong><br />
              Holding parsed segment values and intermediate totals in mind across multi-component expressions, without external notation
            </td>
          </tr>
          <tr>
            <td>
              <strong>PRIMARY</strong><br />
              <strong>Numerical reasoning and mental arithmetic</strong><br />
              At Advanced and Expert levels: computing multi-step addition and subtraction of large values entirely in mind
            </td>
            <td>
              <strong>SECONDARY</strong><br />
              <strong>Inhibitory control</strong><br />
              Evaluating and rejecting plausible foils — including rule-violation traps and magnitude errors
            </td>
          </tr>
          <tr>
            <td>
              <strong>SECONDARY</strong><br />
              <strong>Metacognitive self-monitoring</strong><br />
              Committing to responses across a set of six without immediate feedback; sustaining confidence calibration and error awareness
            </td>
            <td>
              <strong>SECONDARY</strong><br />
              <strong>Cognitive flexibility</strong><br />
              Shifting between conversion directions and between additive and subtractive parsing rules within a single expression
            </td>
          </tr>
        </tbody>
      </Table>   
      <h3>The Neuroscience</h3>   
      <p>Roman numeral conversion is a rule-based symbolic reasoning task — neuroanatomically distinct from memory retrieval or perceptual processing. The left hemisphere language and symbol-processing networks, including the angular gyrus and supramarginal gyrus of the inferior parietal lobe, are central to the parsing of positional symbolic notation. The prefrontal cortex governs the application of the rule system — particularly the subtractive rule, which requires the participant to evaluate each symbol’s relationship to its neighbor before assigning a value, a conditional reasoning operation that recruits the dorsolateral prefrontal cortex (dlPFC).</p>
      <p>At Expert level, where three Roman numeral expressions must be converted and combined arithmetically, the task recruits the intraparietal sulcus — the region most consistently associated with numerical magnitude processing and mental arithmetic — in addition to the prefrontal working memory system. The deferred feedback structure maintains engagement of the anterior cingulate cortex in ongoing response monitoring across items. The randomization of items across sessions ensures that performance improvements reflect genuine consolidation of the rule system rather than item-specific learning.</p>
      <hr />
      <h2 id="word-theme">Word Theme</h2>
      <h3>How it Works</h3>
      <p>Word Theme presents a set of scrambled words that share a hidden category, plus one outlier that does not belong. The participant must unscramble the words, identify what they have in common, select the correct category from four choices — and, for an added challenge, identify and unscramble the outlier as well. Because the category can often be inferred before all words are decoded, the exercise rewards both careful bottom-up letter analysis and rapid top-down semantic reasoning — two distinct cognitive strategies that can be deployed flexibly depending on the item. </p>
      <h3>Why This is Important: Making Functional Connections </h3>
      <ul>
        <li>Reading comprehension — using context to infer the meaning of partially understood words or sentences</li>
        <li>Conversation — retrieving the right word quickly, especially when a name or term is on the tip of the tongue</li>
        <li>Detecting something out of place — noticing an error on a form, a wrong item in a grocery bag, or an unusual charge on a bill</li>
        <li>Following written instructions — parsing meaning from text that may be ambiguous or poorly formatted</li>
        <li>Organizing and categorizing information — sorting mail, managing files, grouping medications by type or schedule</li>
        <li>Problem-solving in novel situations — forming a hypothesis and testing it against available evidence before committing to a decision</li>
      </ul>
      <h3>Cognitive Domains Targeted</h3>
      <Table striped responsive>
        <tbody>
          <tr>
            <td>
              <strong>PRIMARY</strong><br />
              <strong>Language and lexical retrieval</strong><br />
              Decoding scrambled letter strings by matching candidate sequences against stored word representations — orthographic working memory under search conditions
            </td>
            <td>
              <strong>PRIMARY</strong><br />
              <strong>Semantic reasoning</strong><br />
              Identifying shared conceptual meaning across decoded words; forming and testing category hypotheses against four semantic choices
            </td>
          </tr>
          <tr>
            <td>
              <strong>PRIMARY</strong><br />
              <strong>Working memory</strong><br />
              Holding partial letter sequences, candidate words, and the emerging category hypothesis simultaneously in mind across the full word set
            </td>
            <td>
              <strong>SECONDARY</strong><br />
              <strong>Inhibitory control</strong><br />
              Detecting the outlier requires suppressing the tendency to fit every word into the category — sustained comparative evaluation against a held standard
            </td>
          </tr>
          <tr>
            <td>
              <strong>SECONDARY</strong><br />
              <strong>Cognitive flexibility</strong><br />
              Shifting between bottom-up letter decoding and top-down category-guided hypothesis testing as the task demands
            </td>
            <td>
              <strong>SECONDARY</strong><br />
              <strong>Processing speed</strong><br />
              Efficiency of orthographic search and lexical access; faster word retrieval frees working memory capacity for semantic reasoning and outlier detection
            </td>
          </tr>
        </tbody>
      </Table>   
      <h3>The Neuroscience</h3>
      <p>Word Theme engages an unusually broad network of language and executive systems simultaneously. Unscrambling a word requires the left hemisphere’s orthographic processing regions — the visual word form area in the left fusiform gyrus — to evaluate letter combinations against stored lexical representations, while the phonological loop holds candidate letter sequences in working memory during the search. This is a demanding form of orthographic working memory that is sensitive to vocabulary depth, literacy history, and language background.</p>
      <p>Once candidate words begin to emerge, the semantic network — anchored in the left anterior temporal lobe and angular gyrus — is recruited to extract meaning and build category representations. The prefrontal cortex then governs the hypothesis-testing process: forming a category prediction, holding it in working memory, and using it to constrain further decoding. The outlier detection task adds a further inhibitory control demand: the participant must hold the confirmed category in mind while systematically evaluating each word against it and suppressing the tendency to force ambiguous words into the category. This combination — language, semantic memory, working memory, and executive control operating concurrently — makes Word Theme one of the most cognitively integrative exercises in the suite.</p>
    </WtmContainer>
  );
};

export default WhyThisMatters;